import {
  EnumProductType,
  PurchasedProductGuestsPerTier,
} from "gql/generated";
import { useEffect, useState } from "react";
import { PostHogCustomEvent } from "shared/hooks/usePostHog";
import { useWidgetTrack } from "widgets/shared/WidgetTrackingContext";
import { useBookerContext, useCartContext } from "widgets/booker/contexts";
import { getTotalGuestCount } from "widgets/booker/shared/utils";

export type DateRange = {
  startDateISO: string;
  endDateISO: string;
};

export const useDatesAndGuests = (isGuestPageOpen: boolean) => {
  const { product, booker, isEmbedded, invoiceAdminMode, readOnlySelection } =
    useBookerContext();
  const trackPostHogEvent = useWidgetTrack();
  const [dates, setDates] = useState<DateRange>({
    startDateISO: "",
    endDateISO: "",
  });
  const [guests, setGuests] = useState<PurchasedProductGuestsPerTier[]>([]);
  const [guestNames, setGuestNames] = useState<string[]>([]); // Start with an empty array (user needs to select a count first)
  const [complete, setComplete] = useState({
    dates: false,
    guests: false,
    guestNames: product?.accommodationMetadata?.guestMetadata?.nameEntryRequired
      ? false
      : true,
  });
  const {
    updateCartItem,
    cart,
    updateGuestNames,
    guestNames: cartGuestNames,
    setInvoiceAccommodationDraftGetter,
  } = useCartContext();

  const handleUpdateDates = (date: Partial<DateRange>) => {
    setDates(prev => ({ ...prev, ...date }));
  };

  const completeDates = () => {
    if (product) {
      updateCartItem(product._id, dates);
      setComplete(prev => ({ ...prev, dates: true }));

      if (isEmbedded) {
        trackPostHogEvent(PostHogCustomEvent.DatesSelected, {
          bookerId: booker?._id,
          productId: product._id,
        });
      }
    }
  };

  const resetDates = () => {
    const cartItem =
      product && cart.find(item => item.productId === product?._id);
    if (cartItem) {
      setDates({
        startDateISO: cartItem.startDateISO,
        endDateISO: cartItem.endDateISO,
      });
    }
  };

  const handleUpdateGuests = (g: PurchasedProductGuestsPerTier) => {
    const newGuestTiers = guests.map(tier => {
      if (tier.tierId === g.tierId) {
        return g;
      } else return tier;
    });

    setGuests(newGuestTiers);

    const newGuestCount = getTotalGuestCount(newGuestTiers);

    if (newGuestCount > guestNames.length) {
      const addition: string[] = new Array(
        newGuestCount - guestNames.length,
      ).fill("");
      const newGuestNames = [...guestNames, ...addition];
      setGuestNames(newGuestNames);
    } else if (newGuestCount < guestNames.length) {
      const newGuestNames = guestNames.filter((_, i) => i <= newGuestCount - 1);
      setGuestNames(newGuestNames);
    }
  };

  const completeGuests = () => {
    if (product) {
      updateCartItem(product._id, {
        guestsPerTier: guests,
      });
      updateGuestNames(guestNames);
      setComplete(prev => ({ ...prev, guests: true, guestNames: true }));

      if (isEmbedded) {
        trackPostHogEvent(PostHogCustomEvent.GuestsSelected, {
          bookerId: booker?._id,
          productId: product._id,
        });
      }
    }
  };

  const handleUpdateGuestNames = (name: string, index: number) => {
    const newGuestNames = [...guestNames];
    newGuestNames[index] = name;

    setGuestNames(newGuestNames);
  };

  const resetGuests = () => {
    const cartItem =
      product && cart.find(item => item.productId === product?._id);
    if (cartItem) {
      setGuests(cartItem.guestsPerTier ?? []);
    }
    setGuestNames(cartGuestNames);
  };

  useEffect(() => {
    const cartItem =
      product && cart.find(item => item.productId === product?._id);
    if (cartItem) {
      setDates({
        startDateISO: cartItem.startDateISO,
        endDateISO: cartItem.endDateISO,
      });
      // Keep local guest rows in sync with the cart whenever the overlay is closed
      // (invoice hydrate, date step, initial add-to-cart). If we only synced while
      // the flyout was open, guests stayed [] and GuestCounts rendered nothing while
      // checkout still showed cart.guestsPerTier.
      if (!isGuestPageOpen) {
        setGuests(cartItem.guestsPerTier ?? []);
      }
      // Locked invoice selections can't be re-confirmed in the pickers.
      if (readOnlySelection) {
        setComplete({
          dates: !!cartItem.startDateISO && !!cartItem.endDateISO,
          guests: getTotalGuestCount(cartItem.guestsPerTier ?? []) > 0,
          guestNames: true,
        });
      }
    }
    setGuestNames(cartGuestNames);
  }, [cart, product, cartGuestNames, isGuestPageOpen, readOnlySelection]);

  useEffect(() => {
    if (
      !invoiceAdminMode ||
      !product?._id ||
      product.type !== EnumProductType.Accommodation
    ) {
      setInvoiceAccommodationDraftGetter(null);
      return;
    }

    setInvoiceAccommodationDraftGetter(() => ({
      productId: product._id,
      startDateISO: dates.startDateISO,
      endDateISO: dates.endDateISO,
      guestsPerTier: guests,
      guestNames,
    }));

    return () => setInvoiceAccommodationDraftGetter(null);
  }, [
    invoiceAdminMode,
    product?._id,
    product?.type,
    dates.startDateISO,
    dates.endDateISO,
    guests,
    guestNames,
    setInvoiceAccommodationDraftGetter,
  ]);

  return {
    complete,
    onUpdateDates: handleUpdateDates,
    onUpdateGuests: handleUpdateGuests,
    onUpdateGuestNames: handleUpdateGuestNames,
    dates,
    guests,
    guestNames,
    completeGuests,
    completeDates,
    resetGuests,
    resetDates,
  };
};
